import { useCallback, useState } from 'react';
import { MagnifyingGlass, WarningCircle } from '@phosphor-icons/react';

import { ListingCard, ListingGrid, ListingList } from '../../../../design-system/patterns/market';
import {
  Button,
  Empty,
  SkeletonCard,
  SkeletonRow,
} from '../../../../design-system/components/desktop';
import { formatCurrency, mediaUrl } from '../../../../utils/common';
import { errorText, LOAD_FAILURE } from '../../../../utils/errorText';
import {
  isPriceUnavailable,
  isPriceUnconfirmed,
  PRICE_UNCONFIRMED_TILE_NOTE,
} from '../../../../utils/priceAvailability';

/**
 * The results region for every browse surface: the marketplace grid and a
 * seller's store grid.
 *
 * WHAT WENT AWAY
 * --------------
 * `CardItem` and its 377 line stylesheet, the hand-rolled `CardSkeleton`
 * layout, and the hand-rolled `.no-results` block. The card is now the shared
 * `ListingCard` from `patterns/market`, which is the ONE card in the app and is
 * track adaptive on its own: `ds-pcard` below 1024, `dsk-listing-card` at and
 * above it. This file therefore does not branch on the viewport, because the
 * markup it emits is identical on both tracks and the card picks its own.
 *
 * TWO LIBRARY GAPS, REPORTED RATHER THAN PAPERED OVER
 * ---------------------------------------------------
 * 1. There is no `ds-*` skeleton and no `ds-*` empty state. `mobile/*.css` has
 *    neither; the only ones in the library are `.dsk-skeleton*` and
 *    `.dsk-empty`. So `Skeleton` and `Empty` render below 1024 too, which is
 *    the single deliberate prefix crossing on this page. The fix belongs
 *    upstream as `.ds-skeleton` / `.ds-empty`, not as a fourteenth hand-rolled
 *    skeleton here.
 * 2. Nothing here spins, on purpose. The library does ship `Spinner`, but a
 *    skeleton mirrors the SHAPE of what is arriving and a browse grid has a
 *    known shape, so the skeleton is the better answer and the spinner stays
 *    for the surfaces that have nothing to mirror.
 *
 * `available`, THE AMBIGUITY. On `/marketplace` the API's `available` is
 * `Count(listings)` for that card; on `/seller/:id` it is `Sum(quantity)` of
 * that seller's listings. Same field, two meanings, on tiles that look
 * identical. This component renders whatever `availabilityLabel` the page hands
 * it and never computes one, so each page can say what it actually means. See
 * CONVERSION-MAP.md, Open product questions item 5.
 */

const SKELETON_COUNT = 12;

/** Map one API card onto the shared card's prop API. One place, both pages. */
function listingProps(card, availabilityLabel, priceBasis) {
  /* THE TILE SAYS SO BEFORE THE TAP.
     `isPriceUnavailable` is the SHARED predicate (`utils/priceAvailability.js`)
     that `useAddToCart` blocks on, read here off the tile row itself rather
     than re-derived -- one definition of "blocked", so this cannot drift from
     the guard by someone rewriting the condition in one place only.

     IT IS THE SAME PREDICATE, NOT THE SAME ANSWER. The predicate reads whatever
     `price_stale` the row carries, and `useAddToCart` refuses on the STRICTER
     pair `price_stale === false && price_basis_confirmed === true` -- so a tile
     fed only half the server's answer would say "confirmed" about a listing the
     hook is refusing at that moment. That is a real defect and it was live for
     one round; it is fixed where the row is written, in `SellerStore.jsx`'s
     `patchTileFromRefresh`, which only clears `price_stale` when BOTH fields
     confirm. See that function for why the fix belongs at the patch rather than
     in this predicate.
     ONLY `seller-card-lookup` sends `pricing_mode`/`price_stale` (see the
     "QUICK-ADD GUARD FIELDS" block in `apps/api/apps/cards/views.py`);
     `/marketplace`'s `lookup-cards` sends neither, so browse rows make this
     predicate falsy and browse tiles are untouched. It is deliberately keyed
     on the DATA rather than on whether the page passed a quick-add handler: a
     price the platform could not confirm is a fact about the listing, not
     about whether this particular surface offers a one-tap add.

     THE UNION, AND IT IS THE SAME UNION THE ENTRY GATE READS. The two
     predicates answer different questions off different server fields --
     `price_unconfirmed` from `price_checked_at` (nobody attempted this card
     today) and `price_stale` from `price_retrieved_at` (nothing was retrieved
     inside the sellability horizon) -- and neither set contains the other, so
     reading either one alone leaves a tile that is addable-but-unmarked (the
     buyer meets a price dialog with no warning) or marked-but-freely-addable (a
     caveat the tap does not act on). Both are exactly the mismatch this marker
     exists to prevent; see the long note in `utils/priceAvailability.js`. */
  const priceNote =
    isPriceUnconfirmed(card) || isPriceUnavailable(card) ? PRICE_UNCONFIRMED_TILE_NOTE : undefined;
  const available = Number(card?.available) || 0;
  // Market price is the only price these endpoints return, and it is Rands.
  // `formatCurrency` puts the R on. Nothing downstream formats currency and
  // nothing may ever hand this a dollar amount.
  const figure = card?.market_price != null ? formatCurrency(card.market_price) : undefined;
  // `market_price`, THE SECOND AMBIGUITY, and it works exactly like
  // `available` above. On `/seller/:id` the API overwrites the field with
  // `Min(listing.price)`, so it IS the buy price. On `/marketplace` it is
  // `Card.cached_price_zar`, a catalog reference no listing has to match —
  // browse showed R502.62 for a card whose only listing was R552.88, and
  // every click-through was a price rise the buyer never agreed to. Browse
  // cannot know the buy price (`get_collectr_products` returns no listing
  // minimum), so it prints the reference LABELLED as one instead of passing
  // it off as a price you can pay.
  const isMarket = priceBasis === 'market';

  return {
    image: card?.image_url ? mediaUrl(card.image_url) : undefined,
    imageAlt: card?.product_name || '',
    set: card?.catalog_group || undefined,
    name: card?.product_name,
    rarity: card?.rarity || undefined,
    language: card?.language || undefined,
    cardNumber: card?.card_number || undefined,
    price: isMarket ? undefined : figure,
    marketPrice: isMarket ? figure : undefined,
    availability: availabilityLabel ? availabilityLabel(available, card) : undefined,
    /* "3 listings from R200.00", and ONLY past one.
       This is what makes the quick-add's absence legible: a tile with several
       listings gets no add button, because there is no single listing to add,
       and without this line it looked identical to one that does. At exactly
       one the prefix is noise ("1 listing from R764.15" says nothing the price
       does not), so the tile keeps the bare figure and the button explains
       itself. Only `seller-card-lookup` sends `listing_count`, so on
       `/marketplace` this is undefined and the price row is unchanged. */
    listingCount: Number(card?.listing_count) > 1 ? Number(card.listing_count) : undefined,
    /* The figure above stays exactly as it was; this only qualifies it. */
    priceNote,
  };
}

const MarketplaceGrid = ({
  cards,
  viewMode = 'grid',
  onCardClick,
  cardHref,
  loading = false,
  error,
  onRetry,
  availabilityLabel,
  /* What `market_price` MEANS on this page — see `listingProps`. 'listing' (the
     default) is the seller store, where the API has already replaced the field
     with that seller's cheapest listing, so the tile may print it plainly.
     'market' is `/marketplace`, where it is a catalog reference price and the
     tile must label it rather than pass it off as a price you can pay. */
  priceBasis = 'listing',
  /* Empty copy is the page's business: "nothing matches your filters" and
     "this seller has nothing listed" are different sentences with different
     ways out, which is exactly what `Empty`'s tones encode. */
  emptyTitle = 'No cards found',
  emptyTone = 'default',
  emptyText,
  emptyQuery,
  emptyActions,
  /* Scenario slug for the UNFILTERED empty grid only. The other three states
     are not the page's choice, because what happened is the same whichever
     browse surface it happened on: a failed read is `request-failed`, a typed
     query that matched nothing is `search-matched-nothing`, and a filter that
     excluded everything is `filters-matched-nothing`. */
  emptyIllustration = 'no-listings-yet',
  hasMore,
  loaderRef,
  /* THE COLLECTION HEART. Optional, and the fallback below is deliberate: the
     page that owns this grid does not yet have a saved-cards store to read
     from, and inventing a second one here — a third, counting the wishlist
     page's — is exactly the duplication the shared card exists to prevent. So
     the grid accepts the real source when there is one and otherwise keeps the
     toggle in local state, which is enough for the preview to show that a save
     fills the heart and that the fill stays put while you scroll. It does NOT
     survive a reload, and it is not a store. */
  savedIds,
  onToggleSaved,
  /* THE TILE'S ADD TO CART. Optional, and off by default, so `/marketplace`
     renders exactly as it did.
     Only the seller store passes it, because only there does a tile belong to
     ONE seller. A marketplace tile aggregates listings from many sellers, so
     "add this" would silently pick a seller as well as a listing, and the cart
     and checkout are bucketed per seller. That is a separate decision.
     `quickAddFor` returns the listing to add, or null when the tile is
     ambiguous or sold out -- a card with three listings at three conditions has
     no single right answer, so it gets no button and keeps sending the buyer to
     the card page to choose. */
  quickAddFor,
  onQuickAdd,
  quickAddedIds,
  /* THE LISTING IDS WHOSE ADD IS IN FLIGHT -- `useAddToCart`'s `adding` map,
     which the seller store now reads and this grid turns into a per-tile
     `disabled`. A tap on a blocked tile fronts a bounded upstream refresh that
     can run ~24s, so the button has to stop being a button for the duration.
     Optional and empty by default, so `/marketplace` (which passes no
     quick-add props at all) is unaffected. */
  quickAddPendingIds,
}) => {
  const [localSaved, setLocalSaved] = useState(() => new Set());
  const controlled = savedIds != null;
  const saved = controlled ? new Set(savedIds) : localSaved;

  const toggleSaved = useCallback(
    (id, next) => {
      if (onToggleSaved) onToggleSaved(id, next);
      if (controlled) return;
      setLocalSaved((current) => {
        const updated = new Set(current);
        if (next) updated.add(id);
        else updated.delete(id);
        return updated;
      });
    },
    [controlled, onToggleSaved],
  );

  /* EXACTLY TWO VIEWS: 'grid' and 'list', which is rows. It spent a release as
     a horizontal card rail; the designer saw it and sent it back to rows. The
     VALUE stayed 'list' throughout, because it is persisted in nothing and read
     by nothing outside this pair of files. See `ListingList` in patterns/market
     for the reasoning. */
  const isRows = viewMode === 'list';
  const Wrapper = isRows ? ListingList : ListingGrid;
  const Placeholder = isRows ? SkeletonRow : SkeletonCard;

  if (loading) {
    return (
      <div aria-busy="true">
        {/* A skeleton mirrors the SHAPE of what is arriving, so the two views
            wait behind different placeholders — a row is 12 + 44/(3:4) + 12 =
            ~83px tall and a card is a 3:4 tile plus four lines of text. The
            corrections that make `.dsk-skeleton-row` measure like a LISTING row
            rather than the 48px table row it is vendored as already live in
            app-layer.css, keyed on being inside a `.dsk-listing-list`. */}
        <Wrapper>
          {Array.from({ length: SKELETON_COUNT }).map((_, index) => (
            <Placeholder key={index} />
          ))}
        </Wrapper>
      </div>
    );
  }

  if (error) {
    return (
      <Empty
        icon={WarningCircle}
        illustration="request-failed"
        tone="error"
        title="We could not load these cards"
        text={errorText(error, LOAD_FAILURE)}
        actions={
          onRetry ? (
            <Button variant="ghost" onClick={onRetry}>
              Try again
            </Button>
          ) : null
        }
      />
    );
  }

  if (!cards?.length) {
    return (
      <Empty
        icon={MagnifyingGlass}
        illustration={
          emptyQuery
            ? 'search-matched-nothing'
            : emptyTone === 'filtered'
              ? 'filters-matched-nothing'
              : emptyIllustration
        }
        tone={emptyTone}
        title={emptyTitle}
        text={emptyText}
        query={emptyQuery}
        actions={emptyActions}
      />
    );
  }

  /* Resolved per card rather than inline in the JSX so the "no quick add here"
     case is one early return instead of three ternaries on the element. */
  const quickAddProps = (card) => {
    if (!onQuickAdd || !quickAddFor) return null;
    const listing = quickAddFor(card);
    if (!listing) return null;
    return {
      onQuickAdd: () => onQuickAdd(listing, card),
      inCart: Boolean(quickAddedIds && quickAddedIds.has(listing.id)),
      /* Keyed on the LISTING id, which is what `adding` is keyed on -- a
         catalog card can carry several listings and only the one being added
         is busy. */
      quickAddPending: Boolean(quickAddPendingIds && quickAddPendingIds.has(listing.id)),
    };
  };

  return (
    <>
      <Wrapper>
        {cards.map((card) => (
          <ListingCard
            key={card.id}
            mode={isRows ? 'row' : 'tile'}
            href={cardHref ? cardHref(card) : undefined}
            /* The heart is on EVERY surface that lists cards, which is why it
               is wired here and not on one page: this component is the browse
               grid, the store grid and search results. `wishlist` is what makes
               it render at all — the shared card only draws it when a save is
               actually possible. */
            wishlist
            wishlisted={saved.has(card.id)}
            /* One label on both tracks. The desktop tile draws it as a tooltip
               and both tracks put it on the `aria-label`; it does not flip to
               "Remove" when saved, because with `aria-pressed` the name says
               what the control is for and the state says whether it is on. */
            wishlistLabel="Add to Collection"
            onWishlistToggle={(next, event) => {
              /* A SAVE IS NOT A NAVIGATION, and on the mobile track only this
                 line stops it being one. The card is an `<a>`; the mobile
                 `WishlistHeart` in ProductCard.jsx calls `stopPropagation` but
                 not `preventDefault`, and an anchor's activation behaviour
                 runs after dispatch — stopping the bubble does not cancel it,
                 which is why the desktop `TileHeart` does both. The handler is
                 handed the event precisely so a caller can close that gap.
                 Belongs in the library; see the report on this task. */
              if (event) event.preventDefault();
              toggleSaved(card.id, next);
            }}
            onClick={(event) => {
              if (!onCardClick) return;
              // `href` gives the card a real URL, so middle click and "open in
              // a new tab" work. Preventing the default is what keeps the plain
              // click an in-app navigation on BOTH tracks: react-router's Link
              // skips its own navigation once the event is defaulted, and the
              // mobile track's plain <a> would otherwise reload the whole app.
              event.preventDefault();
              onCardClick(card.id, card);
            }}
            {...listingProps(card, availabilityLabel, priceBasis)}
            {...quickAddProps(card)}
          />
        ))}
      </Wrapper>

      {hasMore && <div ref={loaderRef} />}
    </>
  );
};

export default MarketplaceGrid;
